import { createContext, useContext, type ReactNode } from 'react'

/**
 * AB#2610 — the tenant's units-of-measure catalogue, made available to the field renderer.
 *
 * A `currency` field has rendered as a select since AB#1910; a `unit` field never has, so EVERY unit in
 * the product was free text — material base units, bill-of-material component units, and the document
 * flow's `consumedUom`. On the flow ledger that is arithmetic rather than labelling: open quantity is
 * ordered minus consumed, and under sales-BOM explosion the consumed quantity is in COMPONENT units
 * while the predecessor line is in header units (ADR-0044 §4/§5). A typo there is a wrong number.
 *
 * Deliberately a CONTEXT, matching `lookupCreate` and for the reason recorded there: `fetchEntityOptions`
 * and `fetchLookupOptions` are prop-drilled through ~28 call sites in ViewRenderer, and every one is a
 * chance to miss a branch — a seam that silently fails to arrive in a nested grid or tab slot would give
 * a picker in some places and free text in others, which is worse than not having it.
 *
 * Unlike currency, this list is tenant DATA rather than a static constant, which is why it has to be
 * supplied rather than imported. A host that provides nothing — the designer preview — leaves every unit
 * field exactly as it renders today: a plain text input. Editing is never blocked by a missing catalogue.
 */
export interface UnitOption {
    /** The stored value. The bare code, never the label. */
    code: string
    /** What the option reads as, e.g. "KG — Kilogram". */
    label: string
}

const UnitCatalogueContext = createContext<readonly UnitOption[] | undefined>(undefined)

export function UnitCatalogueProvider(
    { units, children }: { units: readonly UnitOption[] | undefined; children: ReactNode },
) {
    return <UnitCatalogueContext.Provider value={units}>{children}</UnitCatalogueContext.Provider>
}

/**
 * The tenant's units, or undefined when the host wired none (or the fetch has not resolved). An EMPTY
 * array is treated as "no catalogue" by the renderer rather than as an empty select: a tenant that has
 * not configured any units must still be able to type one, not be locked out of the field.
 */
export function useUnitCatalogue(): readonly UnitOption[] | undefined {
    return useContext(UnitCatalogueContext)
}
